import {InfiniteData, useQueryClient} from '@tanstack/react-query';
import {useCallback, useRef, useState} from 'react';
import type {
  ConversationMessageAttachment,
  UnifiedFeedPage,
} from '@/types/conversationMessage';
import useSendMessage from '@/hooks/mutations/conversations/use-send-message';
import {unifiedMessagesQueryKey} from '@/hooks/queries/use-unified-messages/useUnifiedMessages';
import type {PendingAttachment} from '@/hooks/use-message-attachments/useMessageAttachments';

type UseSendMessageWithAttachmentsParams = {
  conversationId: string;
  brandId: string;
  pendingAttachments: PendingAttachment[];
  sendAttachments: (
    messageId: string
  ) => Promise<ConversationMessageAttachment[]>;
  campaignId?: string;
};

function useSendMessageWithAttachments({
  conversationId,
  brandId,
  pendingAttachments,
  sendAttachments,
  campaignId,
}: UseSendMessageWithAttachmentsParams) {
  const [pendingAttachmentMessageIds, setPendingAttachmentMessageIds] =
    useState<Set<string>>(new Set());
  const scrollToBottomAfterSendRef = useRef(false);
  const queryClient = useQueryClient();
  const {mutate: sendMessage, isLoading: isSending} = useSendMessage();

  const handleSend = useCallback(
    (composedMessage: string, onSent: () => void) => {
      if (!composedMessage.trim()) {
        return;
      }
      sendMessage(
        {
          conversationId,
          body: composedMessage.trim(),
          sentFromBrandId: brandId,
        },
        {
          onSuccess: (newMessage) => {
            queryClient.setQueryData(
              unifiedMessagesQueryKey(conversationId, campaignId),
              (old: InfiniteData<UnifiedFeedPage> | undefined) => {
                if (!old?.pages.length) {
                  return old;
                }
                const [firstPage, ...rest] = old.pages;
                return {
                  ...old,
                  pages: [
                    {
                      ...firstPage,
                      items: [
                        {kind: 'message' as const, data: newMessage},
                        ...firstPage.items,
                      ],
                    },
                    ...rest,
                  ],
                };
              }
            );
            scrollToBottomAfterSendRef.current = true;
            onSent();

            if (pendingAttachments.length > 0) {
              setPendingAttachmentMessageIds(
                (prev) => new Set([...prev, newMessage.id])
              );
            }

            sendAttachments(newMessage.id)
              .then((attachments) => {
                setPendingAttachmentMessageIds((prev) => {
                  const next = new Set(prev);
                  next.delete(newMessage.id);
                  return next;
                });
                if (!attachments.length) {
                  return;
                }
                queryClient.setQueryData(
                  unifiedMessagesQueryKey(conversationId, campaignId),
                  (old: InfiniteData<UnifiedFeedPage> | undefined) => {
                    if (!old?.pages.length) {
                      return old;
                    }
                    return {
                      ...old,
                      pages: old.pages.map((page) => ({
                        ...page,
                        items: page.items.map((item) =>
                          item.kind === 'message' &&
                          item.data.id === newMessage.id
                            ? {
                                ...item,
                                data: {
                                  ...item.data,
                                  messageAttachments: attachments,
                                },
                              }
                            : item
                        ),
                      })),
                    };
                  }
                );
              })
              .catch(() => {
                setPendingAttachmentMessageIds((prev) => {
                  const next = new Set(prev);
                  next.delete(newMessage.id);
                  return next;
                });
                // attachment errors are non-fatal; message was already sent
              });
          },
        }
      );
    },
    [
      conversationId,
      brandId,
      sendMessage,
      queryClient,
      sendAttachments,
      pendingAttachments,
      campaignId,
    ]
  );

  return {
    handleSend,
    isSending,
    pendingAttachmentMessageIds,
    scrollToBottomAfterSendRef,
  };
}

export default useSendMessageWithAttachments;
